/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// import dependencies
import { forwardRef } from 'react'
import {
	headingsPlugin,
	listsPlugin,
	quotePlugin,
	thematicBreakPlugin,
	markdownShortcutPlugin,
	diffSourcePlugin,
	toolbarPlugin,
	linkPlugin,
	imagePlugin
} from '@mdxeditor/editor'

// import components
import {
	MDXEditor,
	BlockTypeSelect,
	BoldItalicUnderlineToggles,
	StrikeThroughSupSubToggles,
	ListsToggle,
	CreateLink,
	InsertThematicBreak,
	CodeToggle,
	UndoRedo,
	InsertImage,
	DiffSourceToggleWrapper
} from '@mdxeditor/editor'

// import types
import type { ForwardedRef } from 'react'
import type { ImageUploadHandler, MDXEditorMethods, MDXEditorProps } from '@mdxeditor/editor'
import ClientOnly from './client'

// define types
type EditorProps = {
	imageUploader?: ImageUploadHandler
	images?: string[]
}

// initialize the markdown editor
const Editor = ({
	editorRef,
	imageUploader,
	images,
	...props
}: { editorRef: ForwardedRef<MDXEditorMethods> | null } & EditorProps & MDXEditorProps) => {
	return (
		<div className='w-full rounded border border-800 bg-black outline-none'>
			<MDXEditor
				plugins={[
					headingsPlugin(),
					listsPlugin(),
					quotePlugin(),
					thematicBreakPlugin(),
					markdownShortcutPlugin(),
					diffSourcePlugin(),
					linkPlugin(),
					imagePlugin({
						imageAutocompleteSuggestions: images,
						imageUploadHandler: imageUploader,
					}),
					toolbarPlugin({
						toolbarClassName: 'bg-transparent border-b border-800 p-1',
						toolbarContents: () => (
							<DiffSourceToggleWrapper options={['rich-text', 'source']}>
								<div className='flex flex-wrap gap-2'>
									<BlockTypeSelect />
									<BoldItalicUnderlineToggles />
									<StrikeThroughSupSubToggles />
									<ListsToggle />
									<CreateLink />
									<InsertThematicBreak />
									<CodeToggle />
									<InsertImage />
									<div className='grow'></div>
									<UndoRedo />
								</div>
							</DiffSourceToggleWrapper>
						)
					})
				]}
				{...props}
				ref={editorRef}
				className='dark-theme'
				contentEditableClassName='prose prose-stone prose-invert !max-w-none'
			/>
		</div>
	)
}

// export the markdown editor
export const MarkdownInput = forwardRef<MDXEditorMethods, MDXEditorProps & EditorProps>((props, ref) => <ClientOnly><Editor {...props} editorRef={ref} /></ClientOnly>)
MarkdownInput.displayName = 'MarkdownInput'